import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Avatar,
  Stack,
  Chip,
  Divider,
  Grid,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Paper,
  List,
  ListItem,
  ListItemText,
  RadioGroup,
  FormControlLabel,
  Radio,
  InputAdornment,
  TextField,
  FormLabel,
  Switch,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { API, graphqlOperation } from "aws-amplify";
import { listPricelists } from "../../graphql/queries";
import { updateUser, deleteUser } from "../../graphql/mutations";
import SuperAdminUpdateForm from "../user/SuperAdminUpdateForm";
import DeleteConfirmationDialog from "./DeleteConfirmationDialog";
import { cognitoUserService } from "../../services/cognitoUserService";
import { useUser } from "../../UserContext";
import "./UserDialog.scss";

// Helper function to format date
export const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const UserDialog = ({
  user,
  open,
  onClose,
  onEditUser,
  isSuperAdmin = false,
  onProviderToggle,
}) => {
  const [editing, setEditing] = useState(false);
  const [pricelists, setPricelists] = useState([]);
  const [activePricelistId, setActivePricelistId] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteCognitoDialogOpen, setDeleteCognitoDialogOpen] = useState(false);
  const [priceOffsetType, setPriceOffsetType] = useState(
    user?.priceOffsetType || "percentage",
  );
  const [priceOffsetValue, setPriceOffsetValue] = useState(
    user?.priceOffsetValue || "",
  );
  const theme = useTheme();
  const { userRecord, userGroups } = useUser(); // Get the current logged-in provider's user record and groups

  useEffect(() => {
    if (user && open && userRecord) {
      console.log("UserDialog - userRecord:", userRecord);
      console.log("UserDialog - userGroups:", userGroups);
      console.log(
        "UserDialog - Is current user a provider?",
        isCurrentUserProvider(),
      );
      console.log("UserDialog - current user (customer):", user);
      fetchPricelists();
    } else {
      console.log("UserDialog - Missing data:");
      console.log("user:", user);
      console.log("open:", open);
      console.log("userRecord:", userRecord);
    }
  }, [user, open, userRecord, userGroups]);

  useEffect(() => {
    if (user && pricelists.length > 0) {
      // Only set activePricelistId if it matches one of the user's own pricelists
      // Otherwise default to empty string to avoid out-of-range value errors
      const userOwnsPricelist = pricelists.some(
        (p) => p.id === user.activePricelistID,
      );
      setActivePricelistId(userOwnsPricelist ? user.activePricelistID : "");
    }
  }, [user, pricelists]);

  const fetchPricelists = async () => {
    try {
      // Fetch pricelists owned by the user's provider (the provider who created this user)
      // not by the logged-in user - we're assigning a pricelist from the user's provider
      // Get 200 pricelists sorted by createdAt (most recent first)
      const response = await API.graphql(
        graphqlOperation(listPricelists, {
          filter: { userId: { eq: user.createdByUserId } },
          limit: 200,
          sortDirection: "DESC",
          // Explicitly sort by createdAt to ensure most recent pricelists first
          // This works with DynamoDB's default sorting on createdAt
        }),
      );
      const pricelistsData = response.data.listPricelists.items || [];
      setPricelists(pricelistsData);
    } catch (error) {
      console.error("Error fetching pricelists:", error);
    }
  };

  const handlePricelistChange = async (e) => {
    const newPricelistId = e.target.value;
    try {
      await API.graphql(
        graphqlOperation(updateUser, {
          input: {
            id: user.id,
            activePricelistId: newPricelistId,
          },
        }),
      );
      setActivePricelistId(newPricelistId);
      onEditUser({ ...user, activePricelistId: newPricelistId });
    } catch (error) {
      console.error("Error updating pricelist:", error);
    }
  };

  const handlePriceOffsetTypeChange = async (e) => {
    const offsetType = e.target.value;
    setPriceOffsetType(offsetType);
    setActivePricelistId(pricelists[0]?.id || "");
    setTimeout(() => {
      setActivePricelistId(activePricelistId);
    }, 0);
    try {
      await API.graphql(
        graphqlOperation(updateUser, {
          input: {
            id: user.id,
            priceOffsetType: offsetType,
          },
        }),
      );
      onEditUser({ ...user, priceOffsetType: offsetType });
    } catch (error) {
      console.error("Error updating price offset type:", error);
    }
  };

  const handlePriceOffsetValueChange = (e) => {
    const offsetValue = e.target.value;
    setPriceOffsetValue(offsetValue);
    // Don't save on every keystroke - save only when user clicks save
  };

  const handleSavePriceOffset = async () => {
    // Get current values from state
    const offsetType = priceOffsetType;
    const offsetValue = priceOffsetValue;

    try {
      await API.graphql(
        graphqlOperation(updateUser, {
          input: {
            id: user.id,
            priceOffsetType: offsetType,
            priceOffsetValue:
              offsetValue === "" ? null : parseFloat(offsetValue),
          },
        }),
      );
      onEditUser({
        ...user,
        priceOffsetType: offsetType,
        priceOffsetValue: offsetValue === "" ? null : parseFloat(offsetValue),
      });
      // Show success notification
      console.log("Price offset settings saved successfully");
    } catch (error) {
      console.error("Error saving price offset settings:", error);
    }
  };

  const handleDeleteUser = async () => {
    try {
      await API.graphql(
        graphqlOperation(deleteUser, {
          input: {
            id: user.id,
          },
        }),
      );
      onClose();
    } catch (error) {
      console.error("Error deleting user:", error);
    }
  };

  const handleDeleteUserFromCognito = async () => {
    try {
      await API.graphql(
        graphqlOperation(deleteUser, {
          input: {
            id: user.id,
          },
        }),
      );
      await cognitoUserService.deleteUser(user.email);
      onClose();
    } catch (error) {
      console.error("Error deleting user from Cognito:", error);
    }
  };

  const handleProviderToggle = async () => {
    try {
      await API.graphql(
        graphqlOperation(updateUser, {
          input: {
            id: user.id,
            isProvider: !user.isProvider,
          },
        }),
      );
      onEditUser({ ...user, isProvider: !user.isProvider });
    } catch (error) {
      console.error("Error toggling provider status:", error);
    }
  };

  const isCurrentUserProvider = () => {
    // Check if current user is a provider based on groups
    // SuperAdmin and Admin users are also considered providers for management purposes
    return (
      (userGroups &&
        (userGroups.includes("provider") ||
          userGroups.includes("admin") ||
          userGroups.includes("superadmin"))) ||
      (userRecord && userRecord.isProvider)
    );
  };

  if (!user) return null;

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>User Details</DialogTitle>
        <DialogContent className="user-dialog-content">
          {!editing ? (
            <Stack spacing={2}>
              <Box className="user-header">
                <Avatar
                  className="user-avatar"
                  src={user.companyLogo || "/path/to/placeholder/avatar.png"}
                  alt={user.username || "User Avatar"}
                >
                  {user.username?.charAt(0).toUpperCase() ||
                    user.email?.charAt(0).toUpperCase()}
                </Avatar>
                <Box className="user-info">
                  <Typography className="user-name">
                    {user.username || "User Name"}
                  </Typography>
                  <Typography className="user-email">{user.email}</Typography>
                  <Box className="user-chips">
                    <Chip
                      label={user.subscriptionActive ? "Active" : "Inactive"}
                      color={user.subscriptionActive ? "success" : "error"}
                      size="small"
                    />
                    <Chip
                      label={user.isProvider ? "Provider" : "User"}
                      color={user.isProvider ? "info" : "default"}
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                </Box>
              </Box>

              {/* Show provider toggle if allowed by the parent component */}
              {onProviderToggle && (
                <Box sx={{ mt: 1 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={user.isProvider || false}
                        onChange={onProviderToggle}
                        color="primary"
                        size="small"
                      />
                    }
                    label="Provider Account"
                    sx={{ ml: 1 }}
                  />
                </Box>
              )}

              <Divider className="divider" />

              <Grid container spacing={2} className="user-details-grid">
                <Grid item xs={6}>
                  <Typography className="detail-label">Created</Typography>
                  <Typography className="detail-value">
                    {formatDate(user.createdAt)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography className="detail-label">Last Login</Typography>
                  <Typography className="detail-value">
                    {formatDate(user.lastLogin)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography className="detail-label">
                    Subscription Tier
                  </Typography>
                  <Typography className="detail-value">
                    {user.subscriptionTier || "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography className="detail-label">
                    Subscription Term
                  </Typography>
                  <Typography className="detail-value">
                    {user.subscriptionTerm || "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography className="detail-label">Company Name</Typography>
                  <Typography className="detail-value">
                    {user.companyName || "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography className="detail-label">
                    Company Phone
                  </Typography>
                  <Typography className="detail-value">
                    {user.companyPhone || "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography className="detail-label">Address</Typography>
                  <Typography className="detail-value">
                    {user.address || "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography className="detail-label">City</Typography>
                  <Typography className="detail-value">
                    {user.city || "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography className="detail-label">State</Typography>
                  <Typography className="detail-value">
                    {user.state || "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography className="detail-label">Zip Code</Typography>
                  <Typography className="detail-value">
                    {user.zipCode || "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography className="detail-label">
                    Searches Remaining
                  </Typography>
                  <Typography className="detail-value">
                    {user.searchesRemaining || "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography className="detail-label">
                    Account Status
                  </Typography>
                  <Chip
                    label={
                      user.createdByUserId === "deleted" ? "Deleted" : "Active"
                    }
                    color={
                      user.createdByUserId === "deleted" ? "error" : "success"
                    }
                    size="small"
                  />
                </Grid>
              </Grid>

              {/* Show pricelist selector if the current user is a provider */}
              {/* (providers can assign their own pricelists to any user) */}
              {isCurrentUserProvider() && (
                <Box className="pricelist-select-container">
                  <Typography className="pricelist-select-label">
                    Active Pricelist
                  </Typography>
                  <FormControl fullWidth>
                    {pricelists.length > 0 ? (
                      <Select
                        value={activePricelistId}
                        onChange={handlePricelistChange}
                        displayEmpty
                      >
                        <MenuItem value="">No pricelist</MenuItem>
                        {pricelists.map((pricelist) => (
                          <MenuItem key={pricelist.id} value={pricelist.id}>
                            {pricelist.name}
                          </MenuItem>
                        ))}
                      </Select>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Loading pricelists...
                      </Typography>
                    )}
                  </FormControl>
                </Box>
              )}

              {/* Show price offset controls if the current user is a provider */}
              {isCurrentUserProvider() && (
                <Box className="price-offset-container">
                  <Typography
                    className="price-offset-label"
                    variant="h6"
                    gutterBottom
                  >
                    Price Offset Settings
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <FormControl component="fieldset">
                        <FormLabel component="legend">Offset Type</FormLabel>
                        <RadioGroup
                          row
                          name="priceOffsetType"
                          value={priceOffsetType}
                          onChange={handlePriceOffsetTypeChange}
                        >
                          <FormControlLabel
                            value="percentage"
                            control={<Radio />}
                            label="Percentage (%)"
                          />
                          <FormControlLabel
                            value="fixed"
                            control={<Radio />}
                            label="Fixed Amount ($)"
                          />
                        </RadioGroup>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        id="priceOffsetValue"
                        name="priceOffsetValue"
                        label={
                          priceOffsetType === "percentage"
                            ? "Percentage Offset"
                            : "Fixed Amount Offset"
                        }
                        value={priceOffsetValue}
                        onChange={handlePriceOffsetValueChange}
                        margin="normal"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              {priceOffsetType === "percentage" ? "%" : "$"}
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary">
                        {priceOffsetType === "percentage"
                          ? "A positive percentage will increase prices, a negative percentage will discount prices."
                          : "A positive amount will increase prices, a negative amount will discount prices."}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              )}

              <Box className="recent-pricelists">
                <Typography className="recent-pricelists-title">
                  Recent Pricelists
                </Typography>
                {user.recentPricelists && user.recentPricelists.length > 0 ? (
                  <List>
                    {user.recentPricelists.map((pricelist, index) => (
                      <ListItem key={index} className="pricelist-item">
                        <ListItemText
                          primary={
                            <Typography className="pricelist-name">
                              {pricelist.name}
                            </Typography>
                          }
                          secondary={
                            <Typography className="pricelist-date">
                              {formatDate(pricelist.createdAt)}
                            </Typography>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography className="empty-state">
                    No recent pricelists found
                  </Typography>
                )}
              </Box>

              <Box className="edit-section">
                <Button
                  variant="contained"
                  onClick={() => setEditing(true)}
                  className="edit-button"
                >
                  Edit User
                </Button>
              </Box>
            </Stack>
          ) : (
            <SuperAdminUpdateForm
              user={user}
              onComplete={(updatedUser) => {
                onEditUser(updatedUser);
                setEditing(false);
              }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} className="close-button">
            Close
          </Button>
          <Button
            onClick={handleSavePriceOffset}
            variant="contained"
            color="primary"
            className="save-button"
          >
            Save Changes
          </Button>
          <Button
            onClick={() => setDeleteCognitoDialogOpen(true)}
            className="delete-button"
          >
            Delete from Cognito
          </Button>
          <Button
            onClick={() => setDeleteDialogOpen(true)}
            className="delete-button"
          >
            Delete User Record
          </Button>
        </DialogActions>
      </Dialog>

      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteUser}
        title="Delete User Record"
        description="Are you sure you want to delete this user record? This action cannot be undone."
        confirmButtonText="Delete User Record"
      />

      <DeleteConfirmationDialog
        open={deleteCognitoDialogOpen}
        onClose={() => setDeleteCognitoDialogOpen(false)}
        onConfirm={handleDeleteUserFromCognito}
        title="Delete User from Cognito"
        description="Are you sure you want to delete this user from Cognito? This will permanently delete the user from the authentication system."
        confirmButtonText="Delete from Cognito"
      />
    </>
  );
};

export default UserDialog;
